import { useMemo, useState } from 'react'
import { useCopy } from '@/shared/ui/useCopy'
import {
  composeUrl,
  mailtoUrl,
  COMPOSE_TARGETS,
  type ComposeTarget,
} from '@/shared/util/mailto'
import { mergeTemplate } from '../merge'
import type { Contact, CrmUser, EmailTemplate, Lead, PipelineOutcome, PipelineStage } from '@/types/crm'

type Lang = 'no' | 'en'

/**
 * The one thing a rep came here to do: send the email, then move the lead on.
 *
 * This is the only block in the drawer with a coloured surface, because it is
 * the only one that is an action rather than a reading. Everything else on the
 * page is context for this.
 *
 * Three deliberate choices:
 *
 *   **One language at a time.** Both were shown stacked, which made the block
 *   twice as tall as the rest of the page put together and meant scrolling past
 *   a Norwegian email to reach the English one.
 *
 *   **Copy before mailto.** A mailto link is a lottery — it opens whatever the
 *   OS thinks is a mail client, which for anyone on webmail is nothing at all.
 *   Copy always works, so it goes first and gets the confirmation.
 *
 *   **A named next stage, not a row of chips.** "Move to Contacted" is a
 *   decision; eight equal-weight chips is a menu, and a menu after every email
 *   is how leads end up stuck.
 */
export function NextStep({
  lead,
  template,
  pending,
  contact,
  sender,
  stages,
  currentStage,
  onAdvance,
  onOpenAdmin,
}: {
  lead: Lead
  template: EmailTemplate
  pending: boolean
  contact: Contact | null
  sender: CrmUser | null
  stages: (PipelineStage | PipelineOutcome)[]
  currentStage: PipelineStage | PipelineOutcome
  onAdvance: (stageKey: string) => void
  onOpenAdmin: () => void
}) {
  const [lang, setLang] = useState<Lang>('no')
  const [showEmail, setShowEmail] = useState(false)
  const [pickTarget, setPickTarget] = useState(false)
  /**
   * Where drafts open, remembered per browser.
   *
   * There is no reliable way to detect which mail client somebody uses, and
   * guessing wrong is what produced the original bug — `mailto:` handed to
   * Gmail on iOS silently loses the body. So the rep chooses once and the
   * choice sticks; Gmail is the default because it is the only option that
   * behaves the same on every device.
   */
  const [target, setTarget] = useState<ComposeTarget>(() => {
    const saved = localStorage.getItem('skidaCrm.composeTarget')
    return saved === 'outlook' || saved === 'mailto' ? saved : 'gmail'
  })
  const { copied, copy } = useCopy()

  const chooseTarget = (t: ComposeTarget) => {
    setTarget(t)
    localStorage.setItem('skidaCrm.composeTarget', t)
    setPickTarget(false)
  }

  const merged = useMemo(
    () => mergeTemplate(template, { lead, contact, sender }),
    [template, lead, contact, sender],
  )

  const subject = lang === 'no' ? merged.subjectNo : merged.subjectEn
  const body = lang === 'no' ? merged.bodyNo : merged.bodyEn
  const recipient = contact?.email ?? ''
  const hasEmail = Boolean(subject || body)
  const mail = useMemo(() => mailtoUrl(recipient, subject, body), [recipient, subject, body])
  const openUrl = useMemo(
    () => composeUrl(target, recipient, subject, body),
    [target, recipient, subject, body],
  )
  const targetDef = COMPOSE_TARGETS.find((t) => t.key === target)!

  // The next stage in pipeline order — the forward move, offered by name.
  const next = useMemo(() => {
    const i = stages.findIndex((s) => s.key === currentStage.key)
    return i >= 0 && i < stages.length - 1 ? stages[i + 1] : null
  }, [stages, currentStage])

  const others = stages.filter((s) => s.key !== currentStage.key && s.key !== next?.key)

  return (
    <section className="next">
      <div className="next__head">
        <div>
          <div className="next__label">Next step</div>
          <div className="next__title">{template.title}</div>
        </div>
        {next && (
          <button className="btn btn--primary next__advance" onClick={() => onAdvance(next.key)}>
            Move to {next.label} →
          </button>
        )}
      </div>

      {template.guidance && <p className="next__guidance">{template.guidance}</p>}

      {pending && (
        <p className="next__pending">
          Checking whether anyone here already knows Skida — which of the two opening emails applies
          depends on it.
        </p>
      )}

      {!pending && hasEmail && (
        <>
          <div className="next__actions">
            <button
              className="btn btn--outline"
              onClick={() => setShowEmail((v) => !v)}
              aria-expanded={showEmail}
            >
              {showEmail ? 'Hide email' : 'Suggested email'}
            </button>

            <div className="next__lang" role="group" aria-label="Email language">
              <button
                className={`chip${lang === 'no' ? ' chip--on' : ''}`}
                onClick={() => setLang('no')}
              >
                🇳🇴 Norsk
              </button>
              <button
                className={`chip${lang === 'en' ? ' chip--on' : ''}`}
                onClick={() => setLang('en')}
              >
                🇬🇧 English
              </button>
            </div>

            <div className="next__spacer" />

            <button
              className="btn btn--ghost"
              onClick={() => void copy('body', `${subject}\n\n${body}`)}
            >
              {copied === 'body' ? '✓ Copied' : 'Copy email'}
            </button>
            <div className="next__open">
              <a
                className={`btn btn--ghost${recipient ? '' : ' btn--disabled'}`}
                href={recipient ? openUrl : undefined}
                target={target === 'mailto' ? undefined : '_blank'}
                rel="noreferrer"
                title={recipient ? targetDef.note : 'No contact to address it to'}
              >
                ✉ Open in {targetDef.label}
              </a>
              <button
                className="btn btn--ghost next__openpick"
                onClick={() => setPickTarget((v) => !v)}
                aria-expanded={pickTarget}
                aria-label="Choose where to open drafts"
              >
                ▾
              </button>
              {pickTarget && (
                <div className="openpick">
                  {COMPOSE_TARGETS.map((t) => (
                    <button
                      key={t.key}
                      className={`openpick__item${t.key === target ? ' openpick__item--on' : ''}`}
                      onClick={() => chooseTarget(t.key)}
                    >
                      <span className="openpick__label">{t.label}</span>
                      <span className="openpick__note">{t.note}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {mail.tooLong && target === 'mailto' && (
            <div className="next__missing">
              This email is {mail.length} characters as a link, and some mail clients truncate
              anything over ~1,900 — use <strong>Copy email</strong> instead, or shorten the
              template.
            </div>
          )}

          {merged.missing.length > 0 && (
            <div className="next__missing">
              Unfilled: {merged.missing.map((m) => `[${m}]`).join(', ')} —{' '}
              {merged.missing.some((m) => m === 'Tittel' || m === 'Avsender')
                ? 'set your name and title in Admin → Team.'
                : 'add the contact’s name.'}
            </div>
          )}

          {showEmail && (
            <div className="next__email">
              <div className="next__subject">
                <span className="next__subjectlabel">Subject</span>
                {subject}
                <button className="linkbtn" onClick={() => void copy('subject', subject)}>
                  {copied === 'subject' ? 'copied' : 'copy'}
                </button>
              </div>
              <pre className="next__body">{body}</pre>
              <div className="micro">
                Addressed to {recipient || 'nobody yet — add a contact'}, with the name, company and
                your signature already merged in. Wording lives in{' '}
                <a
                  href="#admin"
                  onClick={(ev) => {
                    ev.preventDefault()
                    onOpenAdmin()
                  }}
                >
                  Admin → Email templates
                </a>
                .
              </div>
            </div>
          )}
        </>
      )}

      <div className="next__moveto">
        <span className="micro">or move to</span>
        {others.map((s) => (
          <button key={s.key} className="chip chip--quiet" onClick={() => onAdvance(s.key)}>
            {s.label}
          </button>
        ))}
      </div>
    </section>
  )
}
